/**
 * src/utils/translateService.js
 *
 * Production-ready translation service for dynamic content (chat, offers, descriptions).
 * Uses LibreTranslate API with robust error handling, fallbacks, and caching.
 *
 * Features:
 * - Graceful degradation when API unavailable
 * - Client-side caching in localStorage
 * - Retry logic with exponential backoff
 * - Health check system to avoid hammering failed APIs
 * - Fallback to original text on all failures
 * - Supports en <-> rw translation primarily
 */
import { requestJson } from "../lib/requestClient";
import { logDev, warnDev } from "./logger";

const TRANSLATE_PROVIDERS = [
  process.env.REACT_APP_TRANSLATE_API_PRIMARY || "https://translate.argosopentech.com/translate",
  process.env.REACT_APP_TRANSLATE_API_FALLBACK || "https://libretranslate.de/translate",
].filter(Boolean);
const CACHE_PREFIX = "farmnet_translation_cache_v1::";
const HEALTH_CHECK_INTERVAL = 5 * 60 * 1000; // 5 minutes
const MAX_RETRIES = 2;
const INITIAL_BACKOFF = 1000; // 1 second
const REQUEST_TIMEOUT_MS = Number(process.env.REACT_APP_TRANSLATE_TIMEOUT_MS || 8000);

// Track API health to avoid hammering failed endpoints
const apiHealthStatus = {
  lastCheckTime: 0,
  isHealthy: true,
  failureCount: 0,
  providerIndex: 0,
};

function logDebug(...args) {
  logDev(...args);
}

function normalizeLang(lang) {
  if (!lang || typeof lang !== "string") return "auto";
  const code = lang.trim().toLowerCase();
  if (code.startsWith("en")) return "en";
  if (code.startsWith("rw") || code.startsWith("kin")) return "rw";
  return code;
}

function guessSourceLang(text) {
  if (!text || typeof text !== "string") return "en";
  const hasKinyarwandaMarkers = /\b(ndi|muraho|amakuru|kandi|ariko|niba|kuko|ndashaka|mbese|mwiriwe|mwakoze|ibyo|uko|ndetse)\b/i.test(text);
  return hasKinyarwandaMarkers ? "rw" : "en";
}

/**
 * Simple stable hash function (djb2 algorithm)
 * Used for generating cache keys
 */
function djb2(str = "") {
  let h = 5381;
  for (let i = 0; i < str.length; i++) {
    h = (h << 5) + h + str.charCodeAt(i);
    h = h & h;
  }
  return (h >>> 0).toString(36);
}

/**
 * Generate a unique cache key for a translation
 */
function getCacheKey(targetLang, text) {
  // Use first 300 chars for hashing (avoid massive strings in key)
  const short = text.length > 300 ? text.slice(0, 300) : text;
  return `${CACHE_PREFIX}${targetLang}::${djb2(short)}`;
}

/**
 * Check if API is healthy before making requests
 * Returns early if API has been failing
 */
function shouldSkipApiCall() {
  const now = Date.now();
  const timeSinceLastCheck = now - apiHealthStatus.lastCheckTime;

  // If API has failed multiple times, skip for a while
  if (apiHealthStatus.failureCount >= 3 && timeSinceLastCheck < HEALTH_CHECK_INTERVAL) {
    logDebug("[TranslateService] API in cooldown period after failures");
    return true;
  }

  // Reset failure count after health check interval
  if (timeSinceLastCheck > HEALTH_CHECK_INTERVAL) {
    apiHealthStatus.failureCount = 0;
    apiHealthStatus.isHealthy = true;
  }

  return false;
}

/**
 * Record API failure for health check
 */
function recordApiFailure(statusCode) {
  apiHealthStatus.lastCheckTime = Date.now();
  apiHealthStatus.failureCount++;
  
  if (statusCode === 403 || statusCode === 401) {
    apiHealthStatus.isHealthy = false;
    warnDev(`[TranslateService] API returned ${statusCode} - disabling translations`);
  }
}

/**
 * Record API success
 */
function recordApiSuccess() {
  apiHealthStatus.failureCount = 0;
  apiHealthStatus.isHealthy = true;
  apiHealthStatus.lastCheckTime = Date.now();
}

/**
 * Internal function to make API call with retry logic
 */
async function callTranslateApi(text, targetLang, sourceLang, attempt = 0, providerStartIndex = apiHealthStatus.providerIndex) {
  const providersCount = TRANSLATE_PROVIDERS.length;
  if (providersCount === 0) return null;

  const normalizedTarget = normalizeLang(targetLang);
  const normalizedSource = normalizeLang(sourceLang);

  for (let i = 0; i < providersCount; i++) {
    const providerIndex = (providerStartIndex + i) % providersCount;
    const provider = TRANSLATE_PROVIDERS[providerIndex];

    const sourceCandidates =
      normalizedSource === "auto"
        ? ["auto", guessSourceLang(text)]
        : [normalizedSource];

    for (const sourceCandidate of sourceCandidates) {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

        const data = await requestJson(provider, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            q: text,
            source: sourceCandidate,
            target: normalizedTarget,
            format: "text",
          }),
          timeoutMs: REQUEST_TIMEOUT_MS,
          retries: MAX_RETRIES,
          operationName: "translateText",
          signal: controller.signal,
        });

        clearTimeout(timeoutId);
        const translated = data?.translatedText || null;

        if (translated) {
          recordApiSuccess();
          apiHealthStatus.providerIndex = providerIndex;
          return translated;
        }

        warnDev("[TranslateService] No translatedText in response");
      } catch (err) {
        const status = Number(err?.status || 0);
        if (status > 0) {
          recordApiFailure(status);
        }

        if (status === 400 && sourceCandidate === "auto" && sourceCandidates.length > 1) {
          continue;
        }

        if (err?.code === "TIMEOUT") {
          warnDev("[TranslateService] Request timeout");
        } else {
          warnDev("[TranslateService] Network or parsing error:", err.message);
        }

        if (attempt < MAX_RETRIES) {
          const backoffDelay = INITIAL_BACKOFF * Math.pow(2, attempt);
          logDebug(`[TranslateService] Network retry after ${backoffDelay}ms`);
          await new Promise((resolve) => setTimeout(resolve, backoffDelay));
          return callTranslateApi(text, normalizedTarget, sourceCandidate, attempt + 1, providerIndex);
        }

        recordApiFailure(status || 0);
      }
    }
  }

  return null;
}

/**
 * Translate text to target language with robust error handling
 *
 * @param {string} text - Text to translate
 * @param {string} targetLang - Target language code (e.g., 'rw', 'en')
 * @param {string} sourceLang - Source language code (optional, defaults to 'auto')
 * @returns {Promise<string|null>} Translated text or null on error
 *
 * Features:
 * - Returns original text immediately if targetLang is 'en'
 * - Checks localStorage cache first
 * - Skips API call if service is in cooldown (after repeated failures)
 * - Calls LibreTranslate API with retry logic
 * - Caches successful result in localStorage
 * - Returns null on API errors (caller should fall back to original)
 * - Gracefully handles 403/401 (access denied) by disabling service
 * - Retries on 429 (rate limit) and 5xx errors
 */
export async function translateText(text, targetLang, sourceLang = "auto") {
  // Validation
  if (!text || typeof text !== "string") return text || null;
  if (!targetLang || typeof targetLang !== "string") return text || null;

  // If target is English, no translation needed
  const normalizedTarget = normalizeLang(targetLang);
  const normalizedSource = normalizeLang(sourceLang);

  if (normalizedTarget === "en") return text;

  const key = getCacheKey(`${normalizedSource}->${normalizedTarget}`, text);

  // Check localStorage cache first (fastest path)
  try {
    const cached = localStorage.getItem(key);
    if (cached) {
      logDebug(`[TranslateService] Cache hit for ${normalizedTarget}`);
      return cached;
    }
  } catch (e) {
    warnDev("[TranslateService] Failed to read cache:", e.message);
    // Continue to API call
  }

  // Skip API if in cooldown period
  if (shouldSkipApiCall()) {
    logDebug("[TranslateService] API in cooldown, returning null");
    return null;
  }

  const translated = await callTranslateApi(text, normalizedTarget, normalizedSource);

  if (translated) {
    // Cache the successful translation
    try {
      localStorage.setItem(key, translated);
    } catch (e) {
      warnDev("[TranslateService] Failed to cache translation:", e.message);
      // Still return the translation even if cache fails
    }
    return translated;
  }

  return null;
}

/**
 * Get current API health status (for UI indicators)
 * @returns {Object} { isHealthy: boolean, failureCount: number }
 */
export function getTranslationApiHealth() {
  return {
    isHealthy: apiHealthStatus.isHealthy,
    failureCount: apiHealthStatus.failureCount,
    inCooldown: shouldSkipApiCall(),
  };
}

/**
 * Clear all cached translations (useful for testing or clearing old cache)
 */
export function clearTranslationCache() {
  try {
    const keys = Object.keys(localStorage);
    keys.forEach((key) => {
      if (key.startsWith(CACHE_PREFIX)) {
        localStorage.removeItem(key);
      }
    });
    logDebug("[TranslateService] Cache cleared");
  } catch (e) {
    warnDev("[TranslateService] Failed to clear cache:", e.message);
  }
}

/**
 * Reset API health status (useful for testing)
 */
export function resetApiHealth() {
  apiHealthStatus.lastCheckTime = 0;
  apiHealthStatus.isHealthy = true;
  apiHealthStatus.failureCount = 0;
  logDebug("[TranslateService] API health reset");
}

export default { 
  translateText, 
  clearTranslationCache, 
  resetApiHealth,
  getTranslationApiHealth,
};

